import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';

import { Link, catchAll } from '@sorare/routing';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { FlippingIconIncrement } from '@sorare/core/src/atoms/animations/FlippingIconIncrement';
import { Promotion } from '@sorare/core/src/atoms/icons/Promotion';
import { Relegation } from '@sorare/core/src/atoms/icons/Relegation';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Portal } from '@sorare/core/src/atoms/layout/Portal';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LabelL, LabelM } from '@sorare/core/src/atoms/typography';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { RivalsLightning } from '@sorare/core/src/components/rivals/RepScore/RivalsLightning';
import {
  FOOTBALL_PLAY,
  FOOTBALL_PLAY_RIVALS_ARENA,
  FOOTBALL_PLAY_RIVALS_LEADERBOARD,
  FOOTBALL_PLAY_RIVALS_MY_LINEUPS,
  FOOTBALL_PLAY_RIVALS_MY_SQUAD,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { isType } from '@sorare/core/src/gql';
import useQuery from '@sorare/core/src/hooks/graphql/useQuery';
import { navLabels, rivals } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { ArenaStory } from 'components/rivals/Arena/ArenaStory';
import { DailyClaimButton } from 'components/rivals/DailyClaimButton';
import { DailyMissions } from 'components/rivals/DailyMissions';
import { PromotionRelegationStory } from 'components/rivals/PromotionRelegationStory';
import { SquadPromotionRelegationStory } from 'components/rivals/SquadPromotionRelegationStory';
import { useRivalsStories } from 'components/rivals/useRivalsStories';

import {
  PlayRivalsTabsQuery,
  PlayRivalsTabsQueryVariables,
} from './__generated__/layout.graphql';

const messages = defineMessages({
  title: {
    id: 'pages.play.rivals.Title',
    defaultMessage: 'Rivals',
  },
});

const StyledTab = styled(Tab)`
  width: 100%;
  @media ${tabletAndAbove} {
    width: auto;
  }
`;

const PLAY_RIVALS_TABS_QUERY = gql`
  query PlayRivalsTabsQuery {
    football {
      id
      rivals {
        id
        currentManager {
          id
          unreadStories {
            id
            ...PromotionRelegationStory_footballRivalsPromotionRelegationStory
            ...SquadPromotionRelegationStory_footballRivalsSquadPromotionRelegationStory
            ...ArenaStory_footballRivalsAggregatedArenaStory
          }
          currentRanking {
            id
            ranking
            points
            divisionChange
          }
          ...ArenaStory_footballRivalsCurrentManager
        }
      }
    }
  }
  ${PromotionRelegationStory.fragments.footballRivalsPromotionRelegationStory}
  ${SquadPromotionRelegationStory.fragments
    .footballRivalsSquadPromotionRelegationStory}
  ${ArenaStory.fragments.footballRivalsAggregatedArenaStory}
  ${ArenaStory.fragments.footballRivalsCurrentManager}
` as TypedDocumentNode<PlayRivalsTabsQuery, PlayRivalsTabsQueryVariables>;

const Layout = () => {
  const { data } = useQuery(PLAY_RIVALS_TABS_QUERY);
  const { currentUser } = useCurrentUserContext();

  const { currentManager } = data?.football.rivals || {};
  const { currentLeaderboard, currentRanking } = currentManager || {};
  const { unreadStories } = currentManager || {};

  const { aggregatedArena, promotionRelegation, squadPromotionRelegation } =
    useRivalsStories(unreadStories);

  const showPromotionRelegationStory = isType(
    promotionRelegation,
    'FootballRivalsPromotionRelegationStory'
  );
  const showSquadPromotionRelegationStory = isType(
    squadPromotionRelegation,
    'FootballRivalsSquadPromotionRelegationStory'
  );
  const showArenaStory =
    !!currentManager &&
    !showPromotionRelegationStory &&
    isType(aggregatedArena, 'FootballRivalsAggregatedArenaStory');

  return (
    <>
      <Portal id="page-header-right-button">
        <Horizontal>
          <DailyClaimButton />
          <DailyMissions />
        </Horizontal>
      </Portal>
      <PageHeader
        backTo={FOOTBALL_PLAY}
        customTitle={
          <Vertical gap={0}>
            <LabelL as="h1" bold>
              <FormattedMessage {...messages.title} />
            </LabelL>
            {currentUser && currentManager && (
              <ConditionalWrapper
                wrap={!!currentLeaderboard}
                Wrapper={Link}
                props={{ to: FOOTBALL_PLAY_RIVALS_LEADERBOARD }}
              >
                <Horizontal gap={0.5}>
                  <FlippingIconIncrement
                    value={currentRanking?.points || 0}
                    renderNumberIncrement={animationProps => (
                      <Horizontal gap={0.5}>
                        <LabelM as="span" bold color="var(--c-neutral-1000)">
                          <AnimatedNumber
                            value={currentManager.currentRanking?.points || 0}
                            animationProps={animationProps}
                          />
                        </LabelM>
                        <RivalsLightning fill="var(--c-rivals-reputation)" />
                      </Horizontal>
                    )}
                  >
                    <>
                      {currentRanking?.divisionChange ===
                        DivisionChange.PROMOTED && (
                        <Promotion fill="var(--c-rivals-friendlies)" />
                      )}
                      {currentRanking?.divisionChange ===
                        DivisionChange.RELEGATED && <Relegation />}
                    </>
                  </FlippingIconIncrement>
                </Horizontal>
              </ConditionalWrapper>
            )}
          </Vertical>
        }
      />
      <ContentWithStickyHeader
        header={
          <TabBar variant="flat" fullWidth>
            <StyledTab
              to={FOOTBALL_PLAY_RIVALS_ARENA}
              matches={[catchAll(FOOTBALL_PLAY_RIVALS_ARENA)]}
            >
              <FormattedMessage {...rivals.arena} />
            </StyledTab>
            <StyledTab
              to={FOOTBALL_PLAY_RIVALS_MY_SQUAD}
              matches={[catchAll(FOOTBALL_PLAY_RIVALS_MY_SQUAD)]}
            >
              <FormattedMessage {...navLabels.mySquad} />
            </StyledTab>
            <StyledTab
              to={FOOTBALL_PLAY_RIVALS_MY_LINEUPS}
              matches={[catchAll(FOOTBALL_PLAY_RIVALS_MY_LINEUPS)]}
            >
              <FormattedMessage {...navLabels.myLineups} />
            </StyledTab>
          </TabBar>
        }
      >
        <Outlet />
      </ContentWithStickyHeader>
      {showPromotionRelegationStory && (
        <PromotionRelegationStory story={promotionRelegation} />
      )}
      {showSquadPromotionRelegationStory && (
        <SquadPromotionRelegationStory story={squadPromotionRelegation} />
      )}
      {showArenaStory && (
        <ArenaStory story={aggregatedArena} manager={currentManager} />
      )}
    </>
  );
};

export default Layout;
